import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ProductCard from '../ProductCard';
import Header from '../Header';

const Collection = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isAnimating, setIsAnimating] = useState(false);

  // Inject responsive styles
  useEffect(() => {
    const styleId = 'collection-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .collection-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin: 0 10px;
        }
        
        @media (max-width: 768px) {
          .collection-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'apparel', label: 'Apparel' },
    { id: 'prints', label: 'Prints' },
  ];

  // Read filter from URL on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['apparel', 'prints'].includes(filterParam)) {
      setActiveFilter(filterParam);
      fetchProducts(filterParam);
    } else {
      fetchProducts('all');
    }
  }, [searchParams]);

  const fetchProducts = async (filter = 'all') => {
    try {
      let query = supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('category', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (filterId) => {
    if (filterId === activeFilter) return;
    
    // Start fade out
    setIsAnimating(true);
    
    // Wait for fade out
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Update filter and fetch new products
    setActiveFilter(filterId);
    await fetchProducts(filterId);
    
    // Start fade in
    setIsAnimating(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0D0D0D',
      fontFamily: "'TASA Orbiter Display', sans-serif",
    }}>
      <Helmet>
        <title>Collection | RTK</title>
        <meta name="description" content="Browse the full RTK collection — art prints and apparel." />
      </Helmet>
      <Header />

      {/* Main Content */}
      <main style={{
        paddingTop: '120px',
        paddingBottom: '40px',
      }}>
        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '20px',
          padding: '0 10px',
          marginBottom: '20px',
        }}>
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleFilterChange(filter.id)}
              style={{
                background: 'none',
                border: 'none',
                color: activeFilter === filter.id ? '#F1F1F1' : '#6F6F6F',
                fontSize: '14px',
                fontWeight: '400',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                cursor: 'pointer',
                padding: 0,
                transition: 'color 0.2s ease',
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#8A8A8A',
          }}>
            Loading products...
          </div>
        ) : products.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#8A8A8A',
          }}>
            No products found.
          </div>
        ) : (
          <div 
            className="collection-grid"
            style={{
              opacity: isAnimating ? 0 : 1,
              transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
          >
            {products.map((product, index) => (
              <div
                key={product.id}
                style={{
                  opacity: isAnimating ? 0 : 1,
                  transform: isAnimating ? 'translateY(20px)' : 'translateY(0)',
                  transition: `opacity 0.3s ease ${index * 0.03}s, transform 0.3s ease ${index * 0.03}s`,
                }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Collection;